<template>
  <v-card
    outlined
    class="mb-2 pa-4 d-flex flex-column justify-space-between definition-card cursor-pointer"
    style="height: 200px"
    data-tourid="routineDefinition-card"
    :ripple="false"
    :class="{ inactive: !definition.isActive }"
    :to="
      account.isAdmin && selectedOrganizationId
        ? {
            name: 'reverAdminRoutineDefinitionView',
            params: {
              organizationId: selectedOrganizationId,
              routineId: definition.id,
            },
          }
        : {
            name: 'routineDefinitionView',
            params: {
              routineId: definition.id,
            },
          }
    "
    @click="handleClick"
  >
    <div class="align-top">
      <div class="d-flex align-center justify-space-between">
        <div
          class="d-flex align-center justify-start me-2"
          :style="{ opacity: definition.isActive ? 1 : 0.6, minWidth: 0 }"
        >
          <div>
            <div
              class="rounded-circle me-3"
              style="width: 8px !important; height: 8px !important"
              :class="definition.isActive ? 'green' : 'yellow'"
            />
          </div>

          <v-card-title class="ma-0 pa-0">
            <p
              class="ma-0 pa-0 blue-grey--text text--darken-3 font-size-16 me-1 definition-name"
            >
              <text-highlight
                highlightClass="yellow lighten-1"
                highlightComponent="span"
                :queries="searchHighlights"
                :title="definition.name"
              >
                {{ definition.name }}
              </text-highlight>
            </p>
          </v-card-title>
          <v-icon
            size="16"
            color="blue-grey"
            v-if="definition.isGloballyAssigned"
          >
            mdi-earth
          </v-icon>
        </div>

        <Menu :options="menuOptions" v-if="menuOptions.length > 1">
          <template v-slot:activator="{ on }">
            <v-btn
              small
              icon
              color="blue-grey darken-1"
              v-on="on"
              @click.stop.prevent="openMenu"
            >
              <v-icon size="16">mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
        </Menu>
        <v-btn
          v-if="isRegularUser"
          icon
          color="blue-grey darken-1"
          @click.stop.prevent="showQRDialog"
        >
          <v-icon size="16">mdi-qrcode</v-icon>
        </v-btn>
      </div>
      <p
        v-snip="3"
        :style="{ opacity: definition.isActive ? 1 : 0.6 }"
        class="caption blue-grey--text text--darken-1 ma-0 pa-0 mt-2"
      >
        {{ definition.description }}
      </p>
    </div>
    <div
      v-if="definition.frequency || definition.type"
      class="d-flex mt-2 align-center"
      :style="{ opacity: definition.isActive ? 1 : 0.6 }"
    >
      <v-chip small outlined class="me-2" v-if="definition.frequency">
        {{ $t(`routines.frequency.${definition.frequency}`) }}
      </v-chip>
      <v-chip small outlined v-if="definition.type">
        {{ $t(`routines.type.${definition.type}`) }}
      </v-chip>
    </div>
    <div
      class="d-flex justify-space-between align-center mt-2"
      :style="{ opacity: definition.isActive ? 1 : 0.6 }"
    >
      <ReminderChip
        v-if="hasReminder"
        class="me-1"
        :recurrence="reminderRecurrence"
        :reminderId="reminderId"
        :isRepeatedReminder="isRepeatedReminder"
        :isLoadingReminder="loading"
        @deleteReminder="handleDeleteReminder"
        @openReminderModal="showRoutineReminder"
      />
      <v-btn
        v-else
        small
        text
        rounded
        color="blue"
        class="me-1 text-truncate flex-shrink-1 overflow-hidden"
        style="white-space: nowrap; text-overflow: ellipsis;"
        data-tourid="routineDefinition-card-addReminder-button"
        :loading="loading"
        :disabled="!definition.isActive"
        @click.stop.prevent="showRoutineReminderModal = true"
      >
        <v-icon size="16" class="me-2">mdi-bell-ring-outline</v-icon>
        <p class="ma-0 pa-0 text-truncate" style="display: inline-block;">
          {{ $t('routines_reminder.set_reminder') }}
        </p>
      </v-btn>

      <div class="d-flex">
        <v-tooltip top max-width="241" v-if="!canStartRoutine">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" class="me-1" color="blue-grey darken-1" size="16">
              mdi-help-circle-outline
            </v-icon>
          </template>
          {{ $t('routines.start_routine_helper') }}
        </v-tooltip>
        <v-btn
          small
          color="blue"
          rounded
          id="start-routine-button"
          data-tourid="routineDefinition-card-startRoutine-button"
          :disabled="!canStartRoutine"
          @click.stop.prevent="startRoutine"
        >
          <v-icon size="16" class="ms-n1 me-1 white--text" color="white"
            >mdi-play</v-icon
          >
          <p class="white--text ma-0 pa-0">
            {{ $t('common.button.start') }}
          </p>
        </v-btn>
      </div>
    </div>
    <RoutineReminderModal
      v-if="showRoutineReminderModal"
      :show="showRoutineReminderModal"
      :isEditMode="hasReminder"
      :reminderId="reminderId"
      :recurrence="reminderRecurrence"
      :reminder="reminders"
      :routineDefinitionId="definition.id"
      :isRepeatedReminder="isRepeatedReminder"
      @deleteReminder="handleDeleteReminder"
      @saveReminder="fetchReminder"
      @close="showRoutineReminderModal = false"
    />
    <RoutineStartDialog
      :open="showStartRoutineDialog"
      :routine="definition"
      @cancel="showStartRoutineDialog = false"
      @started="
        navigateTo({
          name: 'routineExecutionDetail',
          params: { routineId: $event },
        })
      "
    />
    <QRCodeDialog
      data-testid="routineList-modal-qrModal"
      :routineDefinition="definition"
      :open="showShareQRDialog"
      @close="showShareQRDialog = false"
    />
    <CannotDeleteDialog
      :open="showCannotDeleteDialog"
      @cancel="showCannotDeleteDialog = false"
    />
    <DeleteConfirmationModal
      :show="showDeleteDialog"
      :isLoading="deletingRoutine"
      :title="$t('routines.delete_dialog_title')"
      :text="$t('routines.delete_routine_body')"
      @cancel="showDeleteDialog = false"
      @delete="deleteRoutine"
    />
  </v-card>
</template>

<script>
import ReminderChip from '@/containers/RoutineDefinitionsView/RoutineReminder/ReminderChip.vue'
import RoutineReminderModal from './RoutineReminder/RoutineReminderModal.vue'
import RoutineStartDialog from '@/components/RoutineStartModal.vue'
import Menu from '@/components/UI/Menu.vue'
import QRCodeDialog from '../../components/Routines/QRCodeDialog.vue'
import CannotDeleteDialog from '../../components/Routines/CannotDeleteDialog.vue'
import DeleteConfirmationModal from '../../components/Modals/DeleteConfirmationModal.vue'

import api from '@/api'
import mixin from '@/mixins/mixin'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin'
import getEntityId from '@/lib/getEntityId'
import { FREQUENCY_TYPE } from '@/lib/frequencies'
import { mapGetters } from 'vuex'
import moment from 'moment'
import _ from 'lodash'

export default {
  name: 'RoutineDefinitionCard',

  mixins: [mixin, trackerMixin, routineDefinitionPermissions],

  components: {
    ReminderChip,
    RoutineReminderModal,
    QRCodeDialog,
    CannotDeleteDialog,
    DeleteConfirmationModal,
    RoutineStartDialog,
    Menu,
  },

  props: {
    definition: {
      type: Object,
      required: true,
    },

    searchHighlights: {
      type: Array,
      default: () => [],
    },

    selectedOrganizationId: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      reminders: [],
      loading: false,
      showCannotDeleteDialog: false,
      showDeleteDialog: false,
      showRoutineReminderModal: false,
      showStartRoutineDialog: false,
      showShareQRDialog: false,
      deletingRoutine: false,
    }
  },

  mounted() {
    this.fetchReminder()
  },

  computed: {
    ...mapGetters({
      account: 'account',
      accountId: 'accountId',
      accountOrganizationId: 'organizationId',
    }),
    organizationId() {
      if (!this.account.isAdmin) return this.accountOrganizationId
      return this.selectedOrganizationId || this.accountOrganizationId
    },

    isOrganizationAdminOrReverAdmin() {
      return this.account.isAdmin || this.account.isOrganizationAdmin
    },

    isRoutineManagerOrSiteAdmin() {
      return this.account.isSiteAdmin || this.account.isRoutineManager
    },

    isRegularUser() {
      return (
        !this.isOrganizationAdminOrReverAdmin &&
        !this.isRoutineManagerOrSiteAdmin
      )
    },

    menuOptions() {
      return _.compact([
        this.canEditRoutine && {
          icon: 'mdi-pencil-outline',
          testId: 'routineList-button-edit',
          tourId: 'routineList-button-edit',
          id: 'edit',
          title: this.$t('common.button.edit'),
          action: () => {
            this.trackRoutineViewNavigate({
              option: 'edit',
              action: 'menu',
              source: 'card',
              id: this.definition.id,
            })
            this.navigateTo({
              name: 'editRoutine',
              params: { routineDefinitionId: this.definition.id },
            })
          },
        },
        this.canCreateRoutine && {
          title: this.$t('common.button.duplicate'),
          testId: 'routineList-button-duplicate',
          tourId: 'routineList-button-duplicate',
          icon: 'mdi-content-copy',
          action: () => {
            this.trackRoutineViewNavigate({
              option: 'duplicate',
              action: 'menu',
              source: 'card',
              id: this.definition.id,
            })
            this.navigateTo({
              name: 'createRoutineDefinition',
              params: {
                routineDefinitionId: this.definition.id,
                duplicate: true,
              },
            })
          },
        },
        {
          title: this.$t('routines.qr.qr_code'),
          testId: 'listMenu-button-qrCode',
          tourId: 'routineList-button-qrCode',
          icon: 'mdi-qrcode',
          action: () => {
            this.trackRoutineViewNavigate({
              action: 'menu',
              option: 'showQRCode',
              source: 'card',
              id: this.definition.id,
            })
            this.showQRDialog()
          },
        },
        this.canDeleteRoutine && {
          title: this.$t('common.button.delete'),
          testId: 'routineList-button-delete',
          tourId: 'routineList-button-delete',
          icon: 'mdi-delete-outline',
          action: async () => {
            this.trackRoutineViewNavigate({
              action: 'menu',
              option: 'delete',
              source: 'card',
              id: this.definition.id,
            })
            this.handleDeleteRoutine()
          },
        },
      ])
    },

    canCreateRoutine() {
      return this.getRoutineDefinitionPermissions({}).create
    },

    canDeleteRoutine() {
      return this.getRoutineDefinitionPermissions({
        routineDefinition: this.definition,
      }).delete
    },

    canStartRoutine() {
      return (
        this.definition.isActive &&
        this.getRoutineDefinitionPermissions({
          routineDefinition: this.definition,
        }).start
      )
    },

    canEditRoutine() {
      return this.getRoutineDefinitionPermissions({
        routineDefinition: this.definition,
      }).edit
    },

    hasReminder() {
      return Boolean(this.reminders?.length > 0)
    },

    reminderRecurrence() {
      return this.reminders[0]?.recurrence ?? {}
    },

    reminderId() {
      return getEntityId(this.reminders[0])
    },

    isRepeatedReminder() {
      if (_.isEmpty(this.reminderRecurrence)) return false
      const untilMoment = moment(this.reminderRecurrence?.until)
      const startMoment = moment(this.reminderRecurrence?.startDate)

      const isNotDaily =
        this.reminderRecurrence.frequency !== FREQUENCY_TYPE.DAILY
      const multipleIntervals = this.reminderRecurrence.interval > 1

      return (
        !this.reminderRecurrence?.until ||
        isNotDaily ||
        multipleIntervals ||
        !untilMoment.isSame(startMoment)
      )
    },
  },

  methods: {
    async deleteRoutine() {
      try {
        this.deletingRoutine = true
        await api.routine.delete(this.organizationId, this.definition.id)
        this.$emit('getRoutines')
        this.showSuccess(this.$t('routines.routine_deleted_success'))
        this.showDeleteDialog = false
        this.trackRoutineDelete({
          action: 'delete',
          id: this.definition.id,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.deletingRoutine = false
      }
    },

    cancelDelete() {
      this.trackRoutineDelete({
        action: 'cancelDelete',
      })
    },

    async handleDeleteRoutine() {
      const routineExecutions = await this.getRoutineExecutions()
      if (routineExecutions.length) {
        this.showCannotDeleteDialog = true
      } else {
        this.showDeleteDialog = true
      }
    },

    async getRoutineExecutions() {
      try {
        const response = await api.routine.getRoutineExecutions(
          this.organizationId,
          {
            filters: {
              routineDefinition: this.definition.id,
            },
            limit: 1,
            page: 1,
          }
        )
        return response?.data?.results ?? []
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    showRoutineReminder() {
      this.trackRoutineViewNavigate({
        action: 'openReminderModal',
        source: 'card',
      })
      this.showRoutineReminderModal = true
    },

    showQRDialog() {
      this.trackRoutineQrView({
        source: 'card',
      })
      this.showShareQRDialog = true
    },

    async handleDeleteReminder(reminderId) {
      try {
        this.loading = true
        await api.reminder.deleteReminder(this.organizationId, reminderId)
        this.showRoutineReminderModal = false
        await this.fetchReminder()
        this.showSuccess(this.$t('routines_reminder.deleted_successfully'))
        this.trackRoutineDefinitionReminder('reminder.delete', {
          process: 'delete',
          entityType: 'routineDefinition',
          reminderId: reminderId,
        })
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.loading = false
      }
    },

    async fetchReminder() {
      try {
        this.loading = true
        const response = await api.reminder.getReminder(this.organizationId, {
          filters: {
            account: this.accountId,
            entityType: 'routineDefinition',
            entityId: this.definition.id,
          },
        })

        this.reminders = response.data?.results
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.loading = false
      }
    },

    startRoutine() {
      this.trackRoutineViewNavigate({
        action: 'goToStart',
        source: 'card',
        id: this.definition.id,
      })
      this.showStartRoutineDialog = true
    },

    openMenu() {
      this.trackRoutineViewNavigate({
        action: 'openMenu',
        source: 'card',
        id: this.definition.id,
      })
    },

    handleClick() {
      this.trackRoutineViewNavigate({
        action: 'goToRoutineDefinition',
        source: 'card',
        id: this.definition.id,
      })
    },
  },
}
</script>

<style lang="scss">
#start-routine-button.v-btn--disabled {
  .v-icon::before {
    color: white !important;
  }
}

.inactive {
  &:hover {
    cursor: default !important;
  }
}

.definition-card {
  &:hover {
    background-color: $color-blue-lighten2;
  }
}

.definition-name {
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.44px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
